/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export interface AccessTokenRequestBody {
  username: string;
  password: string;
  optIn2FA?: boolean;
  dialingCode?: string;
  phoneNumber?: string;
  otp?: string;
  status?: string;
  deviceId?: string;
  supportsDirectLogin?: boolean;
  restoreAccount?: boolean;
}

export interface PasswordLessEmailBody {
  username: string;
  status?: string;
}

export interface PasswordlessResponse {
  id: string;
  createdAt: string;
  email?: string;
}

export interface VerifyEmailOtpRequest {
  username: string;
  otp: string;
  status?: string;
}

export interface VerifyEmailOtpResponse {
  username: string;
  userId: string;
  accessToken: string;
  refreshToken: string;
}

export interface AccountDeletionResponse {
  deleteScheduledAt?: string;
}

export interface MergeEligibilityResponse {
  isEligible: boolean;
}

export interface CreditBalanceResponse {
  availableCredits: number | null;
  currencyCode: string | null;
  bigMemberId?: string | null;
}

export interface ActivationKeyRequest {
  email?: string;
}

export interface ActivationKeyResponse {
  username?: string;
}

export interface AddFavouriteMemberResponse {
  id?: string;
}

export interface AddShippingAddressResponse {
  addressId?: string;
}

export interface AppleAuthenticationRequestBody {
  appleId: string;
  authCode: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  restoreAccount?: boolean;
}

export interface KeyCredential {
  id?: string;
  type?: string;
  transports?: string[];
}

export interface AuthenticatePasskeyRequestPublicKey {
  rpId: string;
  challenge: string;
  timeout?: number;
  allowCredentials?: KeyCredential[];
  userVerification?: string;
}

export interface AuthenticatePasskeyResponseDto {
  clientDataJSON?: string;
  authenticatorData?: string;
  signature?: string;
  userHandle?: string;
}

export interface AuthenticatePasskeyResponse {
  id?: string;
  rawId?: string;
  authenticatorAttachment?: string;
  response?: AuthenticatePasskeyResponseDto;
  type?: string;
  clientExtensionResults?: AuthenticationExtensionsClientOutputs;
  userId?: string;
  purpose?: string;
}

export interface AllowCredential {
  id?: string;
  type?: string;
  transports?: string[];
}

export interface AuthenticatorOption {
  rpId?: string;
  challenge?: string;
  allowCredentials?: AllowCredential[];
  timeout?: number;
  userVerification?: string;
}

export interface AuthenticatorOptionResponse {
  updateForm?: boolean;
  userId?: string;
  loyaltyId?: string;
  options?: AuthenticatePasskeyRequestPublicKey;
}

export interface Rp {
  id: string;
  name?: string;
  icon?: string;
}

export interface PassKeyUser {
  id: string;
  name: string;
  icon?: string;
  displayName?: string;
}

export interface PubKeyCredParams {
  type?: string;
  alg?: number;
}

export interface CredProps {
  credProps?: boolean;
}

export interface AuthenticatorSelection {
  authenticatorAttachment?: string;
  requireResidentKey?: boolean;
  residentKey?: string;
  userVerification?: string;
}

export interface AuthenticatorRegistrationOptionResponse {
  user: PassKeyUser;
  challenge: string;
  rp: Rp;
  pubKeyCredParams?: PubKeyCredParams[];
  timeout?: number;
  attestation?: string;
  excludeCredentials?: KeyCredential[];
  extensions?: CredProps;
  authenticatorSelection?: AuthenticatorSelection;
}

export interface Response {
  clientDataJSON?: string;
  attestationObject?: string;
  transports?: string[];
  authenticatorData?: string;
  publicKeyAlgorithm?: number;
  publicKey?: string;
  signature?: string;
  userHandle?: string;
}

export type ClientExtensionResults = Record<string, unknown>;

export interface VerificationAuthenticationResponse {
  id?: string;
  rawId?: string;
  response?: Response;
  type?: string;
  clientExtensionResults?: ClientExtensionResults;
  authenticatorAttachment?: string;
  userId?: string;
}

export interface AuthenticatorVerificationRequestBody {
  verificationAuthenticationResponse?: AuthenticatePasskeyResponse;
  updateForm?: boolean;
  loyaltyId?: string;
  status?: string;
}

export interface AuthenticatorVerificationResponse {
  authenticatorVerificationToken: string;
}

export interface BigMemberSearchResponse {
  username?: string;
  loyaltyId?: string;
  loyaltyTier?: string;
  navitaireCustomerNumber?: string;
  encrypted?: string;
}

export interface ChangeUserNameRequest {
  newUsername: string;
  emailLanguageCode: string;
}

export interface ClientKeyResponse {
  id?: string;
  apiKey?: string;
}

export interface CountryInfo {
  cultureCode?: string;
  countryCode3C?: string;
  dialingCode?: string;
  countryCode?: string;
  name?: string;
  defaultCurrencyCode?: string;
}

export interface CountriesInfo {
  popularCountries: CountryInfo[];
  countries: CountryInfo[];
}

export interface CountrySettingsResponse {
  mobileLogin: boolean;
}

export interface CountryState {
  Name?: string;
  ProvinceStateCode?: string;
  Id?: string;
}

export interface CreatePasskeyResponse {
  rawId?: string;
  authenticatorAttachment?: string;
  type?: string;
  id?: string;
  response?: Response;
  /** Same shape as Chrome `navigator.credentials.create` → `/registration/verification`. */
  clientExtensionResults?: ClientExtensionResults;
  userId?: string;
  deviceId?: string;
}

export interface DecodeResponse {
  iss?: string;
  exp?: string;
  iat?: string;
  sub?: string;
  clientId?: string;
  userId?: string;
  type?: string;
  sessionId?: string;
}

export interface FacebookAuthenticationRequestBody {
  facebookAuthToken: string;
  optIn2FA: boolean;
  status?: string;
  restoreAccount?: boolean;
}

export interface FavouriteMember {
  dateOfBirth?: string;
  docNo?: string;
  expirationDate?: string;
  firstName?: string;
  gender?: string;
  id?: string;
  issuingCountry?: string;
  lastName?: string;
  loyaltyId?: string;
  nationality?: string;
  navitaireCustomerNumber?: string;
  title?: string;
  seq?: number;
  countryCode?: string;
  username?: string;
}

export interface FavouriteMemberResponse {
  favoriteMembers?: FavouriteMember[];
}

export interface ForgotPasswordRequestBody {
  email: string;
  emailLanguageCode?: string;
  mobilePhone?: string;
  dialingCode?: string;
  otp?: string;
}

export interface GenerateOtpResponse {
  createdAt?: string;
  verificationToken?: string;
}

export interface GoogleAuthToken {
  error_description?: string;
  aud?: string;
}

export interface GoogleAuthenticationRequestBody {
  googleAuthToken: string;
  googleClientId: string;
  optIn2FA: boolean;
  status?: string;
  restoreAccount?: boolean;
}

export interface IdTokenResponseBody {
  idToken: string;
}

export interface MobileLoginRequest {
  mobilePhone: string;
  restoreAccount?: boolean;
}

export interface MobileLoginResponse {
  createdAt: string;
  verificationToken: string;
}

export interface NoContentResponse {
  statusCode?: string;
}

export interface Configs {
  isEnforcedPostLogin?: boolean;
  isGeneralOtpLimitEnabled?: boolean;
  generalOtpLimit?: number;
  isEnabledForPublic?: boolean;
  isEnabledForUser?: boolean;
}

export interface OTPConfigurationResponse extends Configs {
  phoneVerification?: Configs;
  smsServiceProvider: Configs;
}

export interface OTPGenerateByEmailRequestBody {
  email: string;
  cultureCode: string;
  purpose: string;
}

export interface OTPGenerateByWhatsappRequestBody {
  mobilePhone: string;
  cultureCode: string;
  platform: string;
  purpose: string;
}

export type PhoneVerificationChannel = 'sms' | 'whatsapp';

export function isPhoneVerificationChannel(
  value: unknown,
): value is PhoneVerificationChannel {
  return value === 'sms' || value === 'whatsapp';
}

/** Keep only known OTP channels from an API / route payload. */
export function filterPhoneVerificationChannels(
  channels: unknown,
): PhoneVerificationChannel[] {
  if (!Array.isArray(channels)) return [];
  return channels.filter(isPhoneVerificationChannel);
}

export interface OTPGenerateByUserIdResponse {
  otp?: string;
  createdAt?: string;
}

export interface OTPGenerateRequestBody {
  email: string;
  dialingCode: string;
  phoneNumber: string;
  cultureCode?: string;
  purpose?: string;
}

export interface OTPVerifyByLoggedInUserRequestBody {
  otp: string;
  dialingCode: string;
  mobilePhone: string;
}

export interface OTPVerifyBySocialRequestBody {
  dialingCode: string;
  phoneNumber: string;
  otp: string;
  firstName: string;
  lastName: string;
  gender: string;
  state: string;
}

export interface OTPVerifyByUserEmailRequestBody {
  otp: string;
  username: string;
  password: string;
  dialingCode: string;
  phoneNumber: string;
}

export interface OTPVerifyRequestBody {
  email: string;
  dialingCode: string;
  phoneNumber: string;
  otp: string;
}

export interface OtpOptionsList {
  list: OtpOptions[];
}

export interface OtpOptionsArray {
  array: OtpOptions[];
}

export interface PhoneNumberVerficationResponse {
  isPhoneVerificationRequired?: boolean;
  availableChannels?: PhoneVerificationChannel[];
}

export interface RegisteredPasskey {
  id: string;
  name: string;
  deviceId?: string;
  icon?: string;
  iconUrl?: string;
  createdAt?: number;
  aaguid?: string;
  credentialDeviceType?: string;
  updatedAt?: number;
  authCounter?: number;
  lastUsedAt?: number;
  isThisDevice?: boolean;
}

export interface RequestOtpBody {
  dialingCode: string;
  phoneNumber: string;
  cultureCode?: string;
}

export interface ShippingAddress {
  area?: string;
  label?: string;
  contactName?: string;
  contactNumber?: string;
  isPreferred?: boolean;
  country?: string;
  countryCode?: string;
  state?: string;
  shippingAddressId?: string;
  postalCode?: string;
  place?: string;
  dialingCode?: string;
  phoneNumber?: string;
}

export interface SignUpRequestBody {
  username: string;
  password: string;
  addressLine1?: string;
  navitaireCustomerNumber?: string;
  city?: string;
  country?: string;
  dateOfBirth?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  dialingCode?: string;
  phoneNumber?: string;
  mobilePhone?: string;
  nationality?: string;
  postalCode?: string;
  notificationPreference?: boolean;
  cultureCode?: string;
  state?: string;
  title?: string;
  parentGuardian?: string;
  parentGuardianDocNumber?: string;
  parentGuardianEmail?: string;
  parentGuardianFullName?: string;
  loyaltyId?: string;
  loyaltyTier?: string;
  paxType?: string;
  sendEmail?: boolean;
}

export interface SignUpResponse {
  id?: string;
}

export enum SsoErrors {
  INVALID_CREDENTIALS,
  INVALID_REFRESH_TOKEN,
  USER_LOCKED,
  USER_DELETED,
  USER_TERMINATED,
  USER_NOT_ACTIVATED,
  USER_SCHEDULED_FOR_DELETION,
  UNAUTHORIZED,
  FACEBOOK_ACCOUNT_WITH_NO_EMAIL,
  METHOD_IS_NOT_ALLOWED,
  MOBILE_NUMBER_LINKED_TO_ANOTHER_ACCOUNT,
  USER_HAS_CREDIT,
  INVALID_VALUE,
  INVALID_2FA_TOKEN,
  USER_NOT_REGISTERED,
  USER_NOT_VERIFIED,
  INVALID_USERNAME,
  INVALID_MOBILE_PHONE,
  OTP_VERIFICATION_FAIL,
  OTP_EXPIRED,
  OTP_MAX_ATTEMPT_REACHED,
  OTP_COOLDOWN_LIMIT_REACHED,
  USER_AUTHENTICATORS_NOT_FOUND,
  AUTHENTICATION_RESPONSE_VERIFICATION_FAILURE,
  PASSKEY_LOGIN_ERROR,
  PHONE_NUMBER_HAS_MULTIPLE_ACCOUNTS,
  PHONE_NUMBER_REGION_NOT_ALLOWED_FOR_SMS,
  UNREGISTERED_MOBILE_NUMBER,
}

export enum SSOErrorCodes {
  TOKEN_EXPIRED = 'Token provided has Expired!',
}

export interface NationalIdData {
  issueCountry?: string;
  expiryDate?: string;
}

export interface TravelDocResponse {
  expiryDate?: string;
  passportNumber?: string;
  mRZCode?: string;
  passportIssueCountry?: string;
  passportType?: string;
  icNumber?: string;
  ic?: NationalIdData;
}

/** Single item from GET /um/v2/users/{userId}/security-activity */
export interface SecurityActivityApiItem {
  id?: string;
  userChangeRefId?: string;
  action?: string | null;
  type?: string;
  activityType?: string;
  createdAt?: string;
  timestamp?: string | number;
  [key: string]: unknown;
}

export interface SecurityActivityResponse {
  items?: SecurityActivityApiItem[];
}

/** Body for PATCH /um/v2/users/{userId}/security-activity/{activityId} */
export type SecurityActivityPatchAction = 'recognised' | 'change-password';

export interface SecurityActivityPatchRequest {
  action: SecurityActivityPatchAction;
}

/** Response from GET /auth/v2/sessions/{sessionId} */
export interface RecentSecurityActivityDetailResponse {
  id?: string;
  device?: string;
  devicePlatform?: string;
  deviceType?: string;
  clientApp?: string;
  location?:
    | string
    | {
        city?: string | null;
        country?: string | null;
      }
    | null;
  accessTime?: string | number;
  createdAt?: string | number;
  lastUsedAt?: string | number;
  type?: string;
  [key: string]: unknown;
}

/**
 * Shared by both 2FA login calls, so every field is optional.
 */
export interface TwoFATokenResponse extends AccessTokenResponse {
  twoFAToken?: string;
  available2FAMethods?: string[];
  isPhoneVerificationRequired?: boolean;
}

export interface UpdateFavouriteMemberResponse {
  memberId?: string;
}

export interface UpdateShippingAddressResponse {
  shippingAddressId?: string;
}

export interface UpdateTravelDocRequest {
  expiryDate?: string;
  passportIssueCountry?: string;
  passportNumber?: string;
  icNumber?: string;
  ic?: NationalIdData;
}

export interface FaceProfile {
  faceId?: string;
  faceStatus?: number;
  timestamp?: string;
}

export interface User {
  username?: string;
  gender?: string;
  notificationPreference?: boolean;
  isInsured?: boolean;
  cultureCode?: string;
  dialingCode?: string;
  mobilePhone?: string;
  status?: string;
  lastName?: string;
  phoneNumberVerified?: boolean;
  updateNamesCount?: number;
  phoneNumber?: string;
  paxType?: string;
  firstName?: string;
  title?: string;
  loyaltyTier?: string;
  id?: string;
  loyaltyId?: string;
  navitaireRegistrationCompletedAt?: string;
  dateOfBirth?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  state?: string;
  clientId?: string;
  navitaireCustomerNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  nationality?: string;
  postalCode?: string;
  emergencyRelationship?: string;
  emergencyPhoneNumber?: string;
  emergencyFirstName?: string;
  emergencyLastName?: string;
  emergencyDialingCode?: string;
  bigMemberId?: string;
  parentGuardian?: string;
  parentGuardianFirstName?: string;
  parentGuardianLastName?: string;
  parentGuardianFullName?: string;
  parentGuardianEmail?: string;
  parentGuardianDocNumber?: string;
  parentGuardianRelationship?: string;
  relationship?: string;
  ipAddress?: string;
  walletId?: string;
  activatedAt?: string | number;
  createdAt?: string | number;
  createdBySocial?: string;
  firstLastNameChangeAt?: string;
  lastUpdatePwdDate?: string;
  updatedBy?: string;
  updateUsernameCount?: string;
  baggageTypeId?: string;
  email?: string;
  cityPreference?: string;
  newsletterIds?: string[];
  otp?: string;
  faceId?: string;
  faceProfile?: FaceProfile;
  bigpayId?: string;
  bigpayLinkExpiresAt?: string;
  twoFactorAuthPreferences?: {
    sms: boolean;
    email: boolean;
  };
}

/**
 * Per-channel preference object. Supports dynamic keys for categories (e.g. travel, delivery)
 * and nested settings (e.g. weekly_newsletters, monthly_newsletters).
 */
export interface NotificationPreferencesConfigChannels {
  sms: boolean;
  email: boolean;
  push: boolean;
}

export interface NotificationPreferencesConfigSubCategory {
  titleCode: string;
  subtitleCode: string;
  internalCode: string;
  channels: NotificationPreferencesConfigChannels;
  applicableCountries: string[];
}

export interface NotificationPreferencesConfigMainCategory {
  titleCode: string;
  shortCode: string;
  subtitleCode: string;
}

export interface NotificationPreferencesConfigCategory {
  mainCategory: NotificationPreferencesConfigMainCategory;
  subCategories: NotificationPreferencesConfigSubCategory[];
}

export type NotificationPreferencesConfig =
  NotificationPreferencesConfigCategory[];

export interface NotificationChannelPreference {
  isActive?: boolean;
  categories?: Record<string, unknown>;
  /** Sub-category short codes the user opted out of for this channel (e.g. `TRAVEL_MONTHLY`). */
  subCategories?: {
    unsubscribed?: string[];
    [key: string]: unknown;
  };
  /** Main-category short codes paused for this channel (e.g. `TRAVEL`). */
  mainCategories?: {
    unsubscribed?: string[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Dynamic map of channel names (e.g. email, push, social) to their preferences.
 */
export type NotificationChannels = Record<
  string,
  NotificationChannelPreference | undefined
>;

export interface UpdateNotificationPreferencesRequest {
  channels: NotificationChannels;
}

export interface NotificationPreferencesResponse {
  /**
   * Whether the user has enabled marketing/notification preferences.
   * (API currently returns this under `notificationPreference`.)
   */
  notificationPreference?: boolean;
  /**
   * Per-channel notification preferences.
   */
  channels?: NotificationChannels;
}

export interface UserUpdateRequestBody {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  country?: string;
  dateOfBirth?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  dialingCode?: string;
  mobilePhone?: string;
  nationality?: string;
  postalCode?: string;
  notificationPreference?: boolean;
  cultureCode?: string;
  state?: string;
  title?: string;
  newsletterIds?: string[];
  parentGuardian?: string;
  parentGuardianDocNumber?: string;
  parentGuardianEmail?: string;
  parentGuardianFirstName?: string;
  parentGuardianLastName?: string;
  parentGuardianFullName?: string;
  emergencyRelationship?: string;
  emergencyDialingCode?: string;
  emergencyPhoneNumber?: string;
  emergencyLastName?: string;
  emergencyFirstName?: string;
  baggageTypeId?: string;
  isInsured?: boolean;
  walletId?: string;
  otp?: string;
}

export interface VerificationByEmailResponse {
  emailOtpAuthToken: string;
}

export interface VerificationByPhoneResponse {
  mobileOtpAuthToken: string;
}

export interface VerificationRequiredRequestBody {
  dialingCode: string;
  phoneNumber: string;
}

export interface PhoneVerificationChannelsRequest {
  mobilePhone: string;
}

export interface VerifyOTPLoginRequest {
  otp: string;
}

export interface VerifyOtpRequestBody {
  email: string;
  otp: string;
  purpose?: string;
}

export interface FlightBookingSearchRequest {
  cultureCode: string;
  device: number;
  isLoggedIn: number;
  lastName: string;
  originCode?: string;
  recordLocator: string;
}

export interface FlightBookingSearchResponse {
  balanceDue: boolean;
  respStatus: string;
  success: boolean;
}

export interface AccessTokenResponse {
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  loyaltyId?: string;
  updateForm?: boolean;
  examples?: string;
  dialingCode?: string;
  mobilePhone?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  state?: string;
  loginMethod?: LoginMethod;
  otpEmail?: string;
}

export interface OTPGenerateByUserIdRequestBody {
  newUsername: string;
  cultureCode: string;
}

export interface UserDetailChangeConfigurationResponse {
  isChangePhoneNumberEnabled?: boolean;
  isChangeUsernameEnabled?: boolean;
  maxFirstNameChangeLength?: number;
  maxLastNameChangeLength?: number;
  maxTotalNameChangeLength?: number;
  userNamesUpdateLimit?: number;
  emailUpdateLimit?: number;
  mobilePhoneUpdateLimit?: number;
  isNameChangeLimitEnabled?: boolean;
  isChangeUsernameViaEmailOtp?: boolean;
}

export interface OTPVerifyByEmailRequestBody {
  otp: string;
}

export interface ShippingAddressList {
  list: ShippingAddress[];
}

export interface OtpOptions {
  name: string;
  isSelected: boolean;
}

export interface TwoFaMethodRequestBody {
  method: string;
}

export interface SocialAuthBody {
  authType: 'login' | 'signup';
  clientId: string;
  gaClientId?: string;
  apiKey?: string;
  provider: SocialProvider;
  restoreAccount?: boolean;
}

export interface SocialAuthResponse {
  method?: SocialProvider;
  accessToken?: string;
  refreshToken?: string;
  userId?: string;
  email?: string;
  name?: string;
  loyaltyId?: string;
  otpForm?: string;
  updateForm?: string;
}

export interface ChangePasswordRequestBody {
  password: string;
  sendEmail: boolean;
  emailLanguageCode: string;
}

export const SOCIAL_PROVIDERS = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
  WECHAT: 'wechat',
} as const;

export type SocialProvider =
  (typeof SOCIAL_PROVIDERS)[keyof typeof SOCIAL_PROVIDERS];

export enum LogoutMethod {
  LOGOUT,
  FORCE_LOGOUT,
  EXPIRED_REFRESH_TOKEN,
  WEB,
}

export enum LoginMethod {
  GOOGLE,
  FACEBOOK,
  APPLE,
  USER_NAME,
  SOCIAL,
  WEB,
  PASSKEY,
  MOBILE,
}

export enum Purpose {
  EMAIL_OTP_VERIFICATION_LOGIN = 'emailOtpVerificationLogin',
  EMAIL_OTP_VERIFICATION_SIGNUP = 'emailOtpVerificationSignUp',
  PHONE_VERIFICATION = 'phoneVerification',
  PHONE_CHANGE = 'phoneChange',
  FORGOT_PASSWORD = 'forgotPassword',
  LOGIN_2FA = 'login2fa',
  USERNAME_CHANGE = 'usernameChange',
  PASSWORD_CHANGE = 'passwordChange',
  DELETE_ACCOUNT = 'deleteAccount',
  UPDATE_2FA = 'update2fa',
}
